<script>
  import Results from "./Results.svelte";
  import Sort from "./Sort.svelte";
  import Loading from "./Loading.svelte";
  import Header from "./Header.svelte";
  import Error from "./Error.svelte";
  import Empty from "./Empty.svelte";
  import Warning from "./Warning.svelte";
  import HamburgerIcon from "./icons/HamburgerIcon.svelte";
  import Filters from "./filters/Filters.svelte";
  import ApiInputForm from "./ApiInputForm.svelte";
  import { ALL, RADICAL, KANJI, VOCABULARY } from "./constants.js";
  import { getCookieValue, isLargeScreen } from "./helpers.js";
  import {
    userLevelStore,
    currentLevelsStore,
    currentViewStore,
    searchParamsStore,
    filterStateStore,
    progressStore,
    descriptionWindowStore
  } from "./stores.js";

  import { getViewableSubjects } from "./filterLogic.js";

  export let name;

  let sessionIsOld = false;
  let session;

  let currentView = localStorage.getItem("userProgress") || RADICAL;
  let currentLevels = [];
  let fetchedData = getData({ initialFetch: true });
  let subjects = [];
  let allSubjects = [];

  async function getData({ levels = currentLevels, initialFetch = false }) {
    let userLevel = localStorage.getItem("userLevel");
    session = getCookieValue("_session");
    if (!session) return (sessionIsOld = true);
    window.scrollTo(0, 0);
    const params = new URLSearchParams();
    params.append("levels", levels);
    if (initialFetch) params.append("initial_fetch", true);
    const res = await fetch(`/subjects?${params.toString()}`);
    const response = await res.json();
    if (response.error) {
      throw { error: response.error };
    } else {
      userLevelStore.set(response.data.current_level);
      currentLevels = response.data.current_level
        .split(",")
        .map(level => parseInt(level));
      currentLevelsStore.set(currentLevels);
      localStorage.setItem("userLevel", response.data.current_level);
      if (response.data.progress) {
        localStorage.setItem("userProgress", response.data.progress.type);
        progressStore.set(response.data.progress);
      }
      allSubjects = response.data.subjects;
      subjects = getViewableSubjects(
        $searchParamsStore,
        response.data.subjects
      );

      return response;
    }
  }

  function handleLevelsChange(event) {
    const levels = event.detail.levels;
    currentLevels = levels;
    fetchedData = getData({ levels });
  }

  function showFilters() {
    window.scrollTo(0, 0);
    if (!isLargeScreen()) document.body.style.overflow = "hidden";
    filterStateStore.set({ active: true });
  }

  function hasWarning(result) {
    return result.too_large;
  }

  function hasSubjects(result) {
    return (
      result &&
      result.data &&
      result.data.subjects &&
      result.data.subjects.length
    );
  }

  function handleSessionChange(event) {
    sessionIsOld = false;
    fetchedData = getData({
      type: currentView,
      levels: currentLevels,
      initialFetch: true
    });
  }

  function closeFiltersIfMobile() {
    if ($filterStateStore.active && !isLargeScreen()) {
      document.body.style.overflow = "";
      filterStateStore.set({ active: false });
    }
  }

  function closeDescriptionWindow() {
    descriptionWindowStore.set({
      ...$descriptionWindowStore,
      active: false
    });
  }

  function processMnemonicText(text) {
    if (!text) return text;
    let processedString = text;
    processedString = processedString.replace(
      /<kanji>/,
      '<span class="description-text-kanji">'
    );
    processedString = processedString.replace(/<\/kanji>/, "</span>");
    processedString = processedString.replace(
      /<vocabulary>/,
      '<span class="description-text-vocabulary">'
    );
    processedString = processedString.replace(/<\/vocabulary>/, "</span>");
    processedString = processedString.replace(/<\/?ja>/g, "");
    return processedString;
  }

  const unsubscribeCurrentViewStore = currentViewStore.subscribe(store => {
    currentView = store === "radicals" ? "radical" : store;
  });

  const unsubscribeSearchParamsStore = searchParamsStore.subscribe(store => {
    window.scrollTo(0, 0);
    subjects = getViewableSubjects(store, allSubjects);
  });
</script>

<style>
  :global(:root) {
    --pink-light: hsl(318, 100%, 88%);
    --pink-hover: hsl(318, 78%, 61%);
    --pink-text-shadow: hsl(316, 78%, 75%);
    --pink-gradient: linear-gradient(to bottom, #f0a, #dd0093);
    --pink-banner: hsl(311, 100%, 96%);
    --pink: hsl(318, 78%, 51%);

    --blue-light: hsl(200, 100%, 90%);
    --blue-hover: hsl(200, 100%, 70%);
    --blue-text-shadow: hsl(197, 53%, 75%);
    --blue-gradient: linear-gradient(to bottom, #0af, #0093dd);
    --blue-banner: hsl(222, 100%, 97%);
    --blue: hsl(200, 100%, 47%);

    --purple-light: hsl(280, 100%, 87%);
    --purple-hover: hsl(280, 100%, 67%);
    --purple-text-shadow: hsl(280, 52%, 75%);
    --purple-gradient: linear-gradient(to bottom, #a0f, #9300dd);
    --purple-banner: hsl(279, 100%, 96%);
    --purple: hsl(280, 100%, 47%);

    --black-light: hsl(0, 0%, 85%);
    --black-hover: hsl(0, 0%, 40%);
    --black-text-shadow: hsl(0, 0%, 75%);
    --black: hsl(0, 0%, 20%);

    --gray: hsl(0, 0%, 67%);
    --gray-second: #d8d8d8;
    --gray-light: hsl(0, 0%, 93%);

    --white: rgb(255, 255, 255);

    --card-box-shadow: 2px 3px 5px rgba(73, 16, 35, 0.2);
    --card-box-shadow-hover: 0px 8px 9px rgba(73, 16, 35, 0.2);
    --button-box-shadow: 2px 3px 9px rgba(112, 40, 45, 0.2);
    --button-tiny-box-shadow: 0px 1px 2px rgba(112, 40, 45, 0.2);

    --font-family-heading: "Signika", sans-serif;
    --font-family-secondary: "Gayathri", sans-serif;
  }

  .color-wrapper {
    background: var(--black);
    padding: 105px 0 25px;
    min-height: calc(100vh - 6rem);
    transition: background 1s;
  }

  .radical {
    background: var(--blue-light);
  }

  .kanji {
    background: var(--pink-light);
  }

  .vocabulary {
    background: var(--purple-light);
  }

  main {
    display: flex;
  }

  h3 {
    font-size: 1.5rem;
    margin-top: 0;
    border-bottom: 1px solid var(--black-light);
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .filter-area {
    position: relative;
    flex: 1;
    transition: all 300ms ease-out;
    z-index: 200;
  }

  .filter-area.hidden {
    flex: 0;
    transform: translate(-25vw, 0);
  }

  .show-filters {
    background: rgba(255, 255, 255, 0.5);
    box-shadow: var(--button-box-shadow);
    border: none;
    color: #888;
    cursor: pointer;
    font-size: 2rem;
    padding: 0 0.5rem;
    position: fixed;
    left: 0;
    top: 6.3rem;
    transform: translate(0, 0);
    z-index: 100;
  }

  .main-content-area {
    flex: 3;
    margin: 0 3.5%;
    min-height: 84vh;
  }

  .description-window-area {
    background: var(--white);
    bottom: 0;
    box-shadow: var(--card-box-shadow);
    min-height: 100px;
    position: fixed;
    padding: 1rem;
    transition: all 0.3s;
    transform: translate(0, 100vh);
    width: calc(66.67% - 2rem);
    z-index: 100;
  }

  .description-window-area.active {
    transform: translate(0, 0);
  }

  .close {
    position: absolute;
    top: 0.5rem;
    right: 1rem;
    background: none;
    font-size: 1.5rem;
    color: #aaa;
    cursor: pointer;
    padding: 0;
    background: rgba(255, 255, 255, 1);
    border: none;
  }

  :global(.description-text-vocabulary) {
    font-weight: bold;
    color: var(--purple);
  }

  :global(.description-text-kanji) {
    font-weight: bold;
    color: var(--pink);
  }

  :global(.description-text-radical) {
    font-weight: bold;
    color: var(--blue);
  }

  @media screen and (max-width: 768px) {
    .show-filters {
      left: 2%;
      top: 1rem;
      background: none;
      border: none;
      box-shadow: none;
      z-index: 400;
    }

    .filter-area {
      flex: 0;
      z-index: 500;
    }

    .filter-area.hidden {
      transform: translate(-92vw, 0);
    }

    .description-window-area {
      width: 84.5%;
    }
  }
</style>

<Header />
<div
  class="color-wrapper"
  class:radical={currentView === RADICAL}
  class:kanji={currentView === KANJI}
  class:vocabulary={currentView === VOCABULARY}
  data-testid="color-wrapper">
  {#if sessionIsOld}
    <ApiInputForm on:sessionUpdated={handleSessionChange} />
  {:else}
    <main>
      <button
        title="show filters"
        aria-label="show filters"
        class="show-filters"
        on:click={showFilters}>
        <HamburgerIcon />
      </button>
      <section
        id="filter-area"
        class="filter-area"
        class:hidden={!$filterStateStore.active}>
        <Filters on:levelsChange={handleLevelsChange} />
      </section>
      <section
        id="main-content"
        class="main-content-area"
        on:click={closeFiltersIfMobile}>
        {#await fetchedData}
          <Loading type={ALL} levels={currentLevels} />
        {:then result}

          {#if hasWarning(result)}
            <Warning />
          {/if}

          {#if hasSubjects(result)}
            <Sort {currentView} />
            <Results {subjects} {currentLevels} />
          {:else}
            <Empty type={currentView} />
          {/if}
        {:catch error}
          <Error message={error} />
        {/await}
        <section
          id="description-window"
          class="description-window-area"
          class:active={$descriptionWindowStore.active}>
          <button class="close" on:click={closeDescriptionWindow}>
            &times;
          </button>
          <h3>
            <span
              class={`description-text-${$descriptionWindowStore.subjectType}`}>
              {$descriptionWindowStore.characters}
            </span>
            &mdash; {$descriptionWindowStore.meaning}
          </h3>
          <p>
            <strong>Meaning</strong>
            :
            {@html processMnemonicText($descriptionWindowStore.meaningMnemonic)}
          </p>
          {#if $descriptionWindowStore.readingMnemonic}
            <p>
              <strong>Reading</strong>
              :
              {@html processMnemonicText($descriptionWindowStore.readingMnemonic)}
            </p>
          {/if}
        </section>
      </section>
    </main>
  {/if}
</div>
